/**
 * Pure parsing of `retention_run_log.results` — a leaf module with NO React /
 * Supabase imports so the jsonb → typed-entries coercion is unit-testable in
 * isolation (mirrors `src/lib/feature-flags/resolve.ts`).
 *
 * The runner (`public.run_retention_cleanup`) appends one entry per category:
 *   { key, status: 'ok'|'error'|'disabled', deleted?, error?, retention_days? }
 * Error text is preserved VERBATIM — surfacing it un-massaged in the admin
 * screen is the point of the run log.
 */

export type RetentionResultStatus = 'ok' | 'error' | 'disabled';

export interface RetentionRunResult {
  key: string;
  status: RetentionResultStatus;
  deleted?: number;
  error?: string;
  retention_days?: number;
}

const STATUSES: readonly RetentionResultStatus[] = ['ok', 'error', 'disabled'];

/** Coerce the raw jsonb `results` array into typed entries, dropping anything malformed. */
export function parseRunResults(raw: unknown): RetentionRunResult[] {
  if (!Array.isArray(raw)) return [];
  const parsed: RetentionRunResult[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) continue;
    const entry = item as Record<string, unknown>;
    if (typeof entry.key !== 'string') continue;
    if (typeof entry.status !== 'string' || !STATUSES.includes(entry.status as RetentionResultStatus)) continue;
    parsed.push({
      key: entry.key,
      status: entry.status as RetentionResultStatus,
      deleted: typeof entry.deleted === 'number' ? entry.deleted : undefined,
      error: typeof entry.error === 'string' ? entry.error : undefined,
      retention_days: typeof entry.retention_days === 'number' ? entry.retention_days : undefined,
    });
  }
  return parsed;
}
